import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications, NotificationItem } from '../../contexts/notificationContext';
import Portal from '../../layout/Portal/Portal';
import { getNotifStyle, getNotifPath } from './notificationHelpers';

interface NotificationPanelProps {
  onClose: () => void;
}

// ── Card color palette per type ──
const cardPalette: Record<string, { accent: string; bg: string }> = {
  approval: { accent: '#10b981', bg: '#ecfdf5' },
  reject: { accent: '#ef4444', bg: '#fef2f2' },
  cr: { accent: '#f59e0b', bg: '#fffbeb' },
  feedback: { accent: '#8b5cf6', bg: '#f5f3ff' },
  default: { accent: '#3b82f6', bg: '#eff6ff' },
};

function getCardStyle(type: string) {
  return cardPalette[type] || cardPalette.default;
}

/**
 * NotificationPanel — Slide panel dari kanan (OffCanvas style) full height.
 * Tampilan modern colorful cards.
 */
const NotificationPanel: React.FC<NotificationPanelProps> = ({ onClose }) => {
  const { notifications, loading, markAsRead, markAllAsRead, refresh, unreadCount } =
    useNotifications();
  const panelRef = useRef<HTMLDivElement>(null);

  // ── Click outside to close ──
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  // ── Prevent body scroll ──
  useEffect(() => {
    document.body.classList.add('overflow-hidden');
    return () => {
      document.body.classList.remove('overflow-hidden');
    };
  }, []);

  const navigate = useNavigate();

  // ── Handle notification click ──
  const handleNotificationClick = async (notif: NotificationItem) => {
    if (!notif.is_read) {
      await markAsRead(notif.id);
    }
    onClose();
    const path = getNotifPath(notif.type);
    navigate(path);
  };

  // ── Time formatting ──
  const formatTime = (isoString: string): string => {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Baru saja';
    if (diffMins < 60) return `${diffMins} menit lalu`;
    if (diffHours < 24) return `${diffHours} jam lalu`;
    if (diffDays < 7) return `${diffDays} hari lalu`;
    return date.toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'short',
    });
  };

  return (
    <Portal>
      {/* Overlay */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.4)',
          backdropFilter: 'blur(4px)',
          zIndex: 1055,
        }}
        onClick={onClose}
      />

      {/* Panel */}
      <div
        ref={panelRef}
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: '420px',
          maxWidth: '100vw',
          background: '#f8fafc',
          zIndex: 1060,
          boxShadow: '-4px 0 32px rgba(0,0,0,0.12)',
          display: 'flex',
          flexDirection: 'column',
          animation: 'panelSlideIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        {/* ── Panel Header ── */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '20px 24px',
            background: '#fff',
            borderBottom: '1px solid #f1f5f9',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div
              style={{
                width: '38px',
                height: '38px',
                borderRadius: '10px',
                background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1rem',
                color: '#fff',
                boxShadow: '0 3px 10px rgba(59,130,246,0.3)',
              }}
            >
              🔔
            </div>
            <div>
              <h5 style={{ margin: 0, fontWeight: 700, fontSize: '1rem', color: '#111827' }}>
                Notifikasi
              </h5>
              {unreadCount > 0 && (
                <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                  {unreadCount} belum dibaca
                </span>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                title="Tandai semua dibaca"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '6px 10px',
                  border: 'none',
                  borderRadius: '8px',
                  background: '#eff6ff',
                  color: '#3b82f6',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  whiteSpace: 'nowrap',
                }}
                onMouseOver={(e) => (e.currentTarget.style.background = '#dbeafe')}
                onMouseOut={(e) => (e.currentTarget.style.background = '#eff6ff')}
              >
                ✓✓ Semua
              </button>
            )}
            <button
              onClick={onClose}
              style={{
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: 'none',
                borderRadius: '8px',
                background: 'transparent',
                color: '#6b7280',
                fontSize: '1.1rem',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onMouseOver={(e) => (e.currentTarget.style.background = '#f3f4f6')}
              onMouseOut={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              ✕
            </button>
          </div>
        </div>

        {/* ── Panel Body ── */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '12px 16px',
          }}
        >
          {/* Loading State */}
          {loading && notifications.length === 0 && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '48px 20px',
                color: '#9ca3af',
              }}
            >
              <div
                style={{
                  width: '32px',
                  height: '32px',
                  border: '3px solid #e5e7eb',
                  borderTopColor: '#3b82f6',
                  borderRadius: '50%',
                  animation: 'panelSpin 0.8s linear infinite',
                  marginBottom: '12px',
                }}
              />
              <span style={{ fontSize: '0.85rem' }}>Memuat...</span>
            </div>
          )}

          {/* Empty State */}
          {!loading && notifications.length === 0 && (
            <div
              style={{
                textAlign: 'center',
                padding: '60px 20px',
              }}
            >
              <div style={{ fontSize: '2.5rem', marginBottom: '12px', opacity: 0.4 }}>🔔</div>
              <div style={{ color: '#6b7280', fontWeight: 500, marginBottom: '4px' }}>
                Belum ada notifikasi
              </div>
              <div style={{ color: '#9ca3af', fontSize: '0.8rem' }}>
                Notifikasi baru akan muncul di sini
              </div>
            </div>
          )}

          {/* Notification Cards */}
          {notifications.map((notif, idx) => {
            const style = getCardStyle(notif.type);
            const nf = getNotifStyle(notif.type);

            return (
              <div
                key={notif.id}
                onClick={() => handleNotificationClick(notif)}
                style={{
                  display: 'flex',
                  gap: '12px',
                  padding: '0',
                  borderRadius: '12px',
                  background: notif.is_read ? '#fff' : style.bg,
                  border: `1px solid ${notif.is_read ? '#f1f5f9' : `${style.accent}20`}`,
                  borderLeft: `3px solid ${style.accent}`,
                  cursor: 'pointer',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  marginBottom: '8px',
                  opacity: notif.is_read ? 0.7 : 1,
                  boxShadow: notif.is_read
                    ? '0 1px 2px rgba(0,0,0,0.03)'
                    : `0 2px 8px ${style.accent}12`,
                  animation: `cardSlideIn 0.3s ease-out ${idx * 0.03}s both`,
                  overflow: 'hidden',
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateX(4px)';
                  e.currentTarget.style.boxShadow = `0 4px 16px ${style.accent}18`;
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateX(0)';
                  e.currentTarget.style.boxShadow = notif.is_read
                    ? '0 1px 2px rgba(0,0,0,0.03)'
                    : `0 2px 8px ${style.accent}12`;
                }}
              >
                {/* Icon */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    padding: '12px 0 12px 12px',
                  }}
                >
                  <div
                    style={{
                      width: '34px',
                      height: '34px',
                      borderRadius: '10px',
                      background: notif.is_read
                        ? '#f3f4f6'
                        : `linear-gradient(135deg, ${style.accent}, ${style.accent}cc)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.9rem',
                      flexShrink: 0,
                      boxShadow: notif.is_read ? 'none' : `0 2px 6px ${style.accent}25`,
                    }}
                  >
                    <span role="img" aria-label={notif.type}>
                      {nf.icon}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div
                  style={{
                    flex: 1,
                    padding: '12px 12px 12px 0',
                    minWidth: 0,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      gap: '6px',
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontWeight: notif.is_read ? 500 : 700,
                          fontSize: '0.85rem',
                          color: '#111827',
                          marginBottom: '2px',
                          lineHeight: 1.3,
                        }}
                      >
                        {notif.title}
                      </div>
                      {notif.body && (
                        <div
                          style={{
                            fontSize: '0.78rem',
                            color: '#6b7280',
                            lineHeight: 1.3,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {notif.body}
                        </div>
                      )}
                    </div>

                    {/* Unread indicator */}
                    {!notif.is_read && (
                      <span
                        style={{
                          display: 'inline-block',
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          background: style.accent,
                          flexShrink: 0,
                          marginTop: '4px',
                        }}
                      />
                    )}
                  </div>

                  <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginTop: '6px' }}>
                    {formatTime(notif.created)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* ── Panel Footer ── */}
        <div
          style={{
            padding: '12px 24px',
            borderTop: '1px solid #f1f5f9',
            textAlign: 'center',
            background: '#fff',
          }}
        >
          <button
            onClick={refresh}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 20px',
              border: '1px solid #e5e7eb',
              borderRadius: '10px',
              background: '#fff',
              color: '#6b7280',
              fontSize: '0.8rem',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = '#f9fafb';
              e.currentTarget.style.borderColor = '#d1d5db';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = '#fff';
              e.currentTarget.style.borderColor = '#e5e7eb';
            }}
          >
            🔄 Segarkan
          </button>
        </div>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes panelSlideIn {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        @keyframes panelSpin {
          to { transform: rotate(360deg); }
        }
        @keyframes cardSlideIn {
          from {
            opacity: 0;
            transform: translateX(12px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
      `}</style>
    </Portal>
  );
};

export default NotificationPanel;
